import { useEffect, useRef } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { useJobsFeed, type Job, type JobStatus, isTerminal } from '@/api/endpoints/jobs'
import { queryKeys } from '@/api/queryKeys'
import { successDescription } from '@/features/jobs/successSummary'
import { preservingChannel } from '@/lib/routing'

/**
 * Background watcher: keeps polling the jobs feed and fires a toast each
 * time a previously-running job transitions to a terminal status. Also
 * primes the "seen" map on first load — terminal jobs present at mount are
 * silenced (we don't spam users with notifications for yesterday's runs).
 *
 * Rendered once, high in the tree (AppLayout). Produces no DOM of its own.
 */
export function JobsWatcher() {
  const { data } = useJobsFeed()
  const navigate = useNavigate()
  const qc = useQueryClient()

  // `seen.current[jobId]` = the last status we saw. On each poll we diff
  // against this map, fire toasts for active→terminal transitions, and
  // then write the new statuses back. Using a ref (not state) because we
  // don't want the diff itself to trigger a render.
  const seen = useRef<Record<string, JobStatus> | null>(null)

  useEffect(() => {
    const jobs = data?.data
    if (!jobs) return

    // First sighting ever — register without firing toasts. Prevents the
    // app from screaming notifications on initial load / refresh.
    if (seen.current === null) {
      const initial: Record<string, JobStatus> = {}
      for (const job of jobs) initial[job.id] = job.status
      seen.current = initial
      return
    }

    const prev = seen.current
    const next: Record<string, JobStatus> = { ...prev }

    for (const job of jobs) {
      const previous = prev[job.id]
      next[job.id] = job.status

      // New job we've never seen before. Announce it only if it's active —
      // a brand-new terminal job is almost certainly pagination revealing
      // an older record, not something just created in this session.
      if (!previous) continue

      // Active → terminal: fire the toast and refresh any caches the job's
      // result is visible in (the mutation onSuccess only fires when the job
      // is *queued*, so list views like the Safety page would otherwise stay
      // stale until a manual refetch).
      if (!isTerminal(previous) && isTerminal(job.status)) {
        notifyTerminal(job, navigate)
        invalidateForJob(job, qc)
      }
    }

    seen.current = next
  }, [data, navigate, qc])

  return null
}

type NavigateFn = ReturnType<typeof useNavigate>

function notifyTerminal(job: Job, navigate: NavigateFn): void {
  const label = jobLabel(job)

  if (job.status === 'succeeded') {
    const firstVideo = firstCompletedVideo(job)
    const description = successDescription(job)
    toast.success(`${label} concluído`, {
      ...(description ? { description } : {}),
      duration: 8_000,
      ...(firstVideo
        ? {
            action: {
              label: 'Abrir',
              onClick: () => {
                void navigate({
                  to: '/projects/$projectId',
                  params: { projectId: firstVideo.id },
                  search: preservingChannel({ lang: firstVideo.language }),
                })
              },
            },
          }
        : {}),
    })
    return
  }

  if (job.status === 'failed') {
    toast.error(`${label} falhou`, {
      description: errorMessage(job) ?? 'Veja os logs para mais detalhes.',
      duration: 10_000,
    })
    return
  }

  if (job.status === 'cancelled') {
    toast.warning(`${label} cancelado`, { duration: 6_000 })
  }
}

function jobLabel(job: Job): string {
  // Friendly labels for the few job types we currently produce; fall back
  // to the raw slug for anything else so new tool jobs still show up.
  const friendly: Record<string, string> = {
    viral_shorts: 'Viral',
    atlas: 'Atlas',
    mirage: 'Mirage',
    pov_lives: 'POV Lives',
    reddit_stories: 'Reddit Stories',
    product_review: 'Review',
    football: 'Futebol',
    nasa_apod: 'APOD',
    quiz: 'Quiz',
    etymology: 'Etimologia',
    ranked: 'Ranked',
    code_levels: 'Code Levels',
    cortes: 'Cortes',
    youtube_upload: 'Upload',
    upload: 'Upload',
    safety_rescan: 'Scan de segurança',
    studio_thumbnail: 'Capas dos Shorts',
    scheduled_upload: 'Publicação',
    studio_engagement: 'Interações pelo Studio',
    'channel_recovery.apply': 'Recuperar canal',
  }
  return friendly[job.type] ?? job.type
}

function invalidateForJob(job: Job, qc: QueryClient): void {
  // Map job.type → which caches its result is visible in. The mutation that
  // kicked the job already invalidated optimistically, but that fired before
  // the worker actually wrote anything; this is the post-completion refresh.
  if (job.type === 'safety_rescan') {
    const detail = job.detail ?? {}
    const videoId = typeof detail['video_id'] === 'string' ? detail['video_id'] : null
    const language = typeof detail['language'] === 'string' ? detail['language'] : null
    void qc.invalidateQueries({ queryKey: queryKeys.videos.all })
    if (videoId && language) {
      void qc.invalidateQueries({ queryKey: queryKeys.safety.verdict(videoId, language) })
      void qc.invalidateQueries({ queryKey: queryKeys.safety.audit(videoId, language) })
    } else {
      void qc.invalidateQueries({ queryKey: queryKeys.safety.all })
    }
  }
}

interface CompletedVideo {
  id: string
  language: string
}

function firstCompletedVideo(job: Job): CompletedVideo | null {
  const raw = (job.detail && typeof job.detail === 'object' ? job.detail['videos'] : null) as
    | unknown[]
    | null
  if (!Array.isArray(raw) || raw.length === 0) return null
  const first = raw[0]
  if (!first || typeof first !== 'object') return null
  const obj = first as Record<string, unknown>
  const id = typeof obj['id'] === 'string' ? obj['id'] : null
  const language = typeof obj['language'] === 'string' ? obj['language'] : null
  if (!id || !language) return null
  return { id, language }
}

function errorMessage(job: Job): string | null {
  const err = job.error
  if (!err || typeof err !== 'object') return null
  const msg = err['message']
  return typeof msg === 'string' && msg.length > 0 ? msg : null
}
